import React, { useState } from 'react';
import imageCompression from 'browser-image-compression';
import './PhotoManager.css';

/**
 * PhotoManager Component
 * Manages up to 3 user photos with client-side compression
 */
const PhotoManager = ({ photos = [], onPhotosChange }) => {
  const [uploading, setUploading] = useState([false, false, false]);
  const [progress, setProgress] = useState([0, 0, 0]);
  const [error, setError] = useState(null);

  // Max 3 photos
  const photoSlots = [0, 1, 2];

  /**
   * Compress image on client side
   */
  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 0.5,              // Target 500KB
      maxWidthOrHeight: 1200,      // Max dimension
      useWebWorker: true,          // Non-blocking
      initialQuality: 0.85,        // 85% quality
      fileType: 'image/jpeg'
    };

    try {
      const compressed = await imageCompression(file, options);
      console.log(`Compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressed.size / 1024 / 1024).toFixed(2)}MB`);
      return compressed;
    } catch (err) {
      throw new Error('Failed to compress image: ' + err.message);
    }
  };

  /**
   * Handle file selection and upload
   */
  const handleFileSelect = async (photoIndex, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
      setError('Please select a JPG or PNG image');
      return;
    }

    // Validate file size (max 2MB before compression)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Photo must be less than 2MB');
      return;
    }

    setError(null);
    const newUploading = [...uploading];
    newUploading[photoIndex] = true;
    setUploading(newUploading);

    try {
      // Step 1: Compress image
      setProgress(prev => {
        const updated = [...prev];
        updated[photoIndex] = 20;
        return updated;
      });

      const compressed = await compressImage(file);

      setProgress(prev => {
        const updated = [...prev];
        updated[photoIndex] = 40;
        return updated;
      });

      // Step 2: Get presigned URL from backend
      const VivahApi = (await import('../../api')).default;
      const uploadResponse = await VivahApi.Photos.generateUploadUrl(photoIndex);
      const { uploadUrl, photoUrl } = uploadResponse.data;

      setProgress(prev => {
        const updated = [...prev];
        updated[photoIndex] = 60;
        return updated;
      });

      // Step 3: Upload to S3
      await VivahApi.Photos.uploadToS3(uploadUrl, compressed);

      setProgress(prev => {
        const updated = [...prev];
        updated[photoIndex] = 80;
        return updated;
      });

      // Step 4: Mark as complete
      await VivahApi.Photos.completeUpload(photoIndex);

      setProgress(prev => {
        const updated = [...prev];
        updated[photoIndex] = 100;
        return updated;
      });

      // Update photos array
      const updatedPhotos = [...photos];
      updatedPhotos[photoIndex] = {
        url: photoUrl,
        orderIndex: photoIndex,
        isPrimary: photoIndex === 0
      };

      if (onPhotosChange) {
        onPhotosChange(updatedPhotos);
      }

      // Reset after a delay
      setTimeout(() => {
        const newUploading = [...uploading];
        newUploading[photoIndex] = false;
        setUploading(newUploading);

        setProgress(prev => {
          const updated = [...prev];
          updated[photoIndex] = 0;
          return updated;
        });
      }, 1000);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed. Please try again.');
      
      const newUploading = [...uploading];
      newUploading[photoIndex] = false;
      setUploading(newUploading);

      setProgress(prev => {
        const updated = [...prev];
        updated[photoIndex] = 0;
        return updated;
      });
    }
  };

  /**
   * Handle photo deletion
   */
  // Confirm modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmIndex, setConfirmIndex] = useState(null);

  const handleDelete = async (photoIndex) => {
    // Open themed confirm modal instead of native confirm
    setConfirmIndex(photoIndex);
    setConfirmOpen(true);
  };

  const cancelConfirm = () => {
    setConfirmOpen(false);
    setConfirmIndex(null);
  };

  const performDelete = async () => {
    const photoIndex = confirmIndex;
    setConfirmOpen(false);
    setConfirmIndex(null);

    try {
      const VivahApi = (await import('../../api')).default;
      await VivahApi.Photos.deletePhoto(photoIndex);

      // Remove photo from array
      const updatedPhotos = [...photos];
      updatedPhotos.splice(photoIndex, 1);

      if (onPhotosChange) {
        onPhotosChange(updatedPhotos);
      }

    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete photo. Please try again.');
    }
  };

  return (
    <div className="photo-manager">
      {confirmOpen && (
        <div className="themed-modal-overlay">
          <div className="themed-modal">
            <div className="modal-title">Delete Photo</div>
            <div className="modal-body">Are you sure you want to delete this photo? This action cannot be undone.</div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={cancelConfirm}>Cancel</button>
              <button className="btn btn-danger" onClick={performDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
      <div className="photo-manager-header">
        <h3>Manage Your Photos</h3>
        <p>Add up to 3 photos. First photo will be your primary photo.</p>
      </div>

      {error && (
        <div className="photo-manager-error">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="photo-grid">
        {photoSlots.map((index) => {
          const photo = photos[index];
          const isUploading = uploading[index];
          const uploadProgress = progress[index];

          return (
            <div key={index} className="photo-card">
              {photo && !isUploading ? (
                // Existing photo
                <div className="photo-preview">
                  <img src={photo.url} alt={`Photo ${index + 1}`} />
                  {index === 0 && <div className="primary-badge">⭐ Primary</div>}
                  <div className="photo-actions">
                    <label className="photo-action-btn" htmlFor={`photo-input-${index}`}>
                      🔄 Replace
                    </label>
                    <button className="photo-action-btn delete" onClick={() => handleDelete(index)}>
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ) : isUploading ? (
                // Uploading state
                <div className="photo-uploading">
                  <div className="upload-spinner">⚙️</div>
                  <div className="upload-progress-bar">
                    <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                  <p>{uploadProgress}% Complete</p>
                </div>
              ) : (
                // Empty slot
                <div className="photo-empty">
                  <label htmlFor={`photo-input-${index}`} className="photo-upload-label">
                    <div className="upload-icon">+</div>
                    <p>Upload Photo {index + 1}</p>
                    <span>Max 2MB</span>
                  </label>
                </div>
              )}

              <input
                id={`photo-input-${index}`}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={(e) => handleFileSelect(index, e)}
                style={{ display: 'none' }}
                disabled={isUploading}
              />
            </div>
          );
        })}
      </div>

      <div className="photo-manager-tips">
        <p>💡 <strong>Tips:</strong></p>
        <ul>
          <li>Your primary photo appears on your profile card and search results</li>
          <li>Use clear, recent photos for best results</li>
          <li>Photos are automatically compressed to optimize loading speed</li>
        </ul>
      </div>
    </div>
  );
};

export default PhotoManager;
